import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, DollarSign, Calendar, Filter, Download, Search } from 'lucide-react';
import apiService from '../services/apiService';

const ZoneContributions = () => {
  const navigate = useNavigate();
  const [contributions, setContributions] = useState({ data: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    year: new Date().getFullYear(),
    month: '',
    status: ''
  });

  useEffect(() => {
    fetchContributions();
  }, [filters]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchContributions = async () => {
    setLoading(true);
    try {
      console.log('Fetching contributions with filters:', filters);
      
      const params = new URLSearchParams();
      if (filters.year) params.append('year', filters.year);
      if (filters.month) params.append('month', filters.month);
      if (filters.status) params.append('status', filters.status);

      console.log('API URL:', `/zone-dashboard/contributions?${params.toString()}`);
      
      const response = await apiService.get(`/zone-dashboard/contributions?${params.toString()}`);
      console.log('API Response:', response);
      
      if (response && response.data) {
        setContributions(response.data);
      } else {
        console.error('Réponse API inattendue:', response);
        setContributions({ data: [], total: 0 });
      }
    } catch (error) {
      console.error('Erreur lors de la récupération des cotisations:', error);
      console.error('Détails de l\'erreur:', error.response);
      
      // Afficher un message d'erreur plus spécifique
      if (error.response?.status === 401) {
        console.error('Erreur d\'authentification - redirection vers login');
      } else if (error.response?.status === 403) {
        console.error('Accès refusé - vérifiez les permissions de zone');
      } else if (error.response?.status === 404) {
        console.error('Endpoint non trouvé - vérifiez que le backend est démarré');
      } else {
        console.error('Erreur lors du chargement des cotisations:', error.message);
      }
      
      setContributions({ data: [], total: 0 });
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'paid': { color: 'green', text: 'Payé' },
      'pending': { color: 'yellow', text: 'En attente' },
      'overdue': { color: 'red', text: 'En retard' }
    };

    const config = statusConfig[status] || statusConfig['pending'];

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-${config.color}-100 text-${config.color}-800`}>
        {config.text}
      </span>
    );
  };

  const monthNames = [
    '', 'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
    'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
  ];

  const totalAmount = Array.isArray(contributions.data) ? contributions.data.reduce((sum, contrib) => sum + (contrib.amount || 0), 0) : 0;
  const paidAmount = Array.isArray(contributions.data) ? contributions.data.filter(contrib => contrib.status === 'paid')
    .reduce((sum, contrib) => sum + (contrib.amount || 0), 0) : 0;

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* En-tête */}
      <div className="mb-8">
        <button
          onClick={() => navigate('/zone-dashboard')}
          className="flex items-center text-blue-600 hover:text-blue-800 mb-4 transition-colors"
        >
          <ArrowLeft className="h-5 w-5 mr-2" />
          Retour au Dashboard
        </button>
        
        <h1 className="text-3xl font-bold text-gray-800 mb-2">Gestion des Cotisations de Zone</h1>
        <p className="text-lg text-gray-600">Gérer les cotisations des membres de votre zone</p>
      </div>

      {/* Statistiques rapides */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-white p-6 rounded-lg shadow-md">
          <div className="flex items-center">
            <div className="p-3 bg-blue-100 rounded-full">
              <DollarSign className="h-6 w-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Attendu</p>
              <p className="text-2xl font-bold text-gray-900">
                {totalAmount.toLocaleString()} F CFA
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-md">
          <div className="flex items-center">
            <div className="p-3 bg-green-100 rounded-full">
              <DollarSign className="h-6 w-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Collecté</p>
              <p className="text-2xl font-bold text-gray-900">
                {paidAmount.toLocaleString()} F CFA
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-md">
          <div className="flex items-center">
            <div className="p-3 bg-yellow-100 rounded-full">
              <Calendar className="h-6 w-6 text-yellow-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Taux de Collecte</p>
              <p className="text-2xl font-bold text-gray-900">
                {totalAmount > 0 ? Math.round((paidAmount / totalAmount) * 100) : 0}%
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Filtres */}
      <div className="bg-white p-6 rounded-lg shadow-md mb-8">
        <h2 className="text-lg font-semibold text-gray-800 mb-4">Filtres</h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Année</label>
            <select
              value={filters.year}
              onChange={(e) => handleFilterChange('year', e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
            >
              {Array.from({ length: 5 }, (_, i) => {
                const year = new Date().getFullYear() - i;
                return (
                  <option key={year} value={year}>{year}</option>
                );
              })}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Mois</label>
            <select
              value={filters.month}
              onChange={(e) => handleFilterChange('month', e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">Tous les mois</option>
              {monthNames.slice(1).map((month, index) => (
                <option key={index + 1} value={index + 1}>{month}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Statut</label>
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">Tous les statuts</option>
              <option value="paid">Payé</option>
              <option value="pending">En attente</option>
              <option value="overdue">En retard</option>
            </select>
          </div>

          <div className="flex items-end">
            <button
              onClick={() => setFilters({ year: new Date().getFullYear(), month: '', status: '' })}
              className="w-full bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg transition-colors"
            >
              Réinitialiser
            </button>
          </div>
        </div>
      </div>

      {/* Liste des cotisations */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <h2 className="text-lg font-semibold text-gray-800">
            Cotisations ({contributions.total || contributions.data?.length || 0})
          </h2>
          <div className="flex space-x-2">
            <button className="flex items-center text-blue-600 hover:text-blue-800 px-3 py-1 border border-blue-600 rounded transition-colors">
              <Download className="h-4 w-4 mr-2" />
              Exporter
            </button>
          </div>
        </div>
        
        {loading ? (
          <div className="flex justify-center items-center h-32">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          </div>
        ) : contributions.data?.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Membre
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Fraternité
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Période
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Montant
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Statut
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date de paiement
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {contributions.data.map((contribution) => (
                  <tr key={contribution.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="h-8 w-8 bg-gray-300 rounded-full flex items-center justify-center">
                          <span className="text-xs font-medium text-gray-700">
                            {contribution.user?.first_name?.charAt(0) || 'U'}{contribution.user?.last_name?.charAt(0) || '?'}
                          </span>
                        </div>
                        <div className="ml-3">
                          <div className="text-sm font-medium text-gray-900">
                            {contribution.user?.first_name || 'N/A'} {contribution.user?.last_name || ''}
                          </div>
                        </div>
                      </div>
                    </td>
                    
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {contribution.user?.fraternity || 'Non assigné'}
                    </td>
                    
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {monthNames[contribution.period_month] || contribution.period_month} {contribution.period_year}
                    </td>
                    
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {contribution.amount?.toLocaleString() || 0} F CFA
                    </td>
                    
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(contribution.status)}
                    </td>
                    
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {contribution.paid_at ? new Date(contribution.paid_at).toLocaleDateString() : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-12 text-gray-500">
            <DollarSign className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>Aucune cotisation trouvée avec ces critères</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ZoneContributions;